/**
 * The rule builder's presentational half: the rules beside the "Matches now"
 * panel, and the verdict on what blocks a count or a save.
 *
 * Split from `RuleSegmentBuilder.tsx` so it imports nothing that reaches the
 * API client. The client reaches `lib/config.ts`, which reads
 * `import.meta.env` and only evaluates inside Vite; everything here is a pure
 * function of its props, so `verify-rule-builder` renders it from fixtures —
 * the counted, the uncounted, the blocked and the zero — rather than checking
 * source text. The container (`RuleSegmentBuilder`) owns the catalogue read,
 * the debounced preview and the save.
 *
 * See `RuleSegmentBuilder.tsx` for why every count here is the API's, why
 * "not counted" is never drawn as 0, and what the concept had that this does
 * not.
 */
import type { FieldDateFormat } from '@wend/shared';
import { Badge, BySourceBars, MetricPanel, MetricSection, NoFigure } from '@wend/ui';
import { useMemo } from 'react';

import {
  RuleGroup,
  countsById,
  incompleteRuleIds,
  issuesById,
  nextRuleField,
  pickableFields,
  previewData,
  toRuleTree,
  type DraftGroup,
  type FieldIndex,
  type PreviewState,
  type RuleTreeActions,
} from '../../components/rules';
import { formatDateTime } from '../../lib/format';

const EN_IN = new Intl.NumberFormat('en-IN');

/** One line the builder and the segment page both use for what "dynamic" means. */
export const DYNAMIC_SENTENCE =
  'Dynamic — updates when data changes. People enter and leave as uploads finish, Reach leads land and replies arrive, and every hour; each entry and exit is logged.';

/* -------------------------------------------------------------------------- */
/* The result panel                                                            */
/* -------------------------------------------------------------------------- */

export interface RuleResultPanelProps {
  readonly preview: PreviewState;
  /** People in the workspace — what the share is of. */
  readonly people: number;
  /** A sentence explaining why nothing can be counted right now, if something blocks it. */
  readonly blocked: string | null;
  readonly className?: string;
}

/**
 * "Matches now": the people the whole tree holds, their share of the
 * workspace, and what each channel would reach. Sticky beside the rules on a
 * wide screen, under them on a phone.
 */
export function RuleResultPanel({ preview, people, blocked, className }: RuleResultPanelProps) {
  const data = previewData(preview);
  const showData = data !== null && blocked === null && preview.status !== 'idle';
  const share = showData && people > 0 ? (data.people / people) * 100 : null;

  const sub = blocked !== null
    ? blocked
    : preview.status === 'idle'
      ? 'Finish a rule and the count appears here.'
      : !showData
        ? preview.status === 'error'
          ? preview.message
          : 'Counting the people these rules hold…'
        : share === null
          ? 'This workspace has no people yet, so there is nothing to take a share of.'
          : `${share.toLocaleString('en-IN', { maximumFractionDigits: 1 })}% of the ${EN_IN.format(people)} people in this workspace`;

  return (
    <MetricPanel
      className={className}
      kicker="Matches now"
      kickerAs="h2"
      live="polite"
      status={
        <Badge tone="brand" dot>
          Dynamic
        </Badge>
      }
      figure={
        showData
          ? { kind: 'measured', value: data.people, unit: data.people === 1 ? 'person' : 'people' }
          : { kind: 'absent', node: <NoFigure state={preview.status === 'pending' ? 'awaiting' : 'not_measured'} /> }
      }
      sub={sub}
      {...(share !== null ? { meter: { value: share / 100 } } : {})}
      data-role="rule-result"
    >
      {showData && (
        <MetricSection kicker="Reachable by channel">
          <BySourceBars
            rows={[
              { key: 'email', label: 'Email', tone: 'ident-a', count: data.emailReachable, of: data.people },
              { key: 'whatsapp', label: 'WhatsApp', tone: 'ident-d', count: data.phoneReachable, of: data.people },
            ]}
          />
          <p className="mt-2 text-[0.72rem] text-muted">
            Distinct addresses, never added together: one person can be on both.
          </p>
        </MetricSection>
      )}
      {preview.status === 'error' && showData && (
        <p role="status" className="mt-3 text-[0.76rem] text-warn">
          The last count could not be refreshed: {preview.message} The figure above is from before your latest change.
        </p>
      )}
      {preview.status === 'pending' && showData && (
        <p className="mt-3 text-[0.72rem] text-muted">Re-counting for your latest change…</p>
      )}
      <MetricSection kicker="How it stays current">
        <p className="text-[0.78rem] leading-relaxed text-muted">{DYNAMIC_SENTENCE}</p>
        {showData && <p className="mt-1.5 font-mono text-[0.68rem] text-muted">Counted {formatDateTime(data.generatedAt)}</p>}
      </MetricSection>
    </MetricPanel>
  );
}

/* -------------------------------------------------------------------------- */
/* The workspace: rules beside the result                                      */
/* -------------------------------------------------------------------------- */

export interface RuleBuilderWorkspaceProps {
  readonly index: FieldIndex;
  readonly tree: DraftGroup;
  readonly actions: RuleTreeActions;
  readonly preview: PreviewState;
  readonly onChooseDateFormat?: (key: string, format: FieldDateFormat) => Promise<void>;
  readonly disabled?: boolean;
}

/** What blocks the count and the save, worked out once for both. */
export function builderVerdict(index: FieldIndex, tree: DraftGroup) {
  const complete = toRuleTree(tree);
  const issues = issuesById(complete, index);
  const incomplete = incompleteRuleIds(tree);
  const blocked =
    issues.size > 0
      ? `${issues.size === 1 ? 'One rule needs' : `${String(issues.size)} rules need`} attention before Wend can count — see the note under ${issues.size === 1 ? 'it' : 'each'}.`
      : null;
  const saveBlocked =
    blocked ??
    (incomplete.size > 0
      ? `Finish or remove ${incomplete.size === 1 ? 'the rule' : `the ${String(incomplete.size)} rules`} marked “needs a value” before saving.`
      : complete === null
        ? 'Add at least one rule before saving.'
        : null);
  return { complete, issues, incomplete, blocked, saveBlocked };
}

export function RuleBuilderWorkspace({ index, tree, actions, preview, onChooseDateFormat, disabled = false }: RuleBuilderWorkspaceProps) {
  const fields = useMemo(() => pickableFields(index), [index]);
  const { issues, blocked } = builderVerdict(index, tree);
  const counts = useMemo(() => countsById(blocked === null ? previewData(preview) : null), [preview, blocked]);
  const issueText = useMemo(() => new Map([...issues].map(([id, issue]) => [id, issue.message])), [issues]);
  const canAdd = nextRuleField(index, null) !== null;

  return (
    <div className="grid items-start gap-[18px] lg:grid-cols-[minmax(0,1.62fr)_minmax(0,1fr)]" data-role="rule-builder">
      <div className="min-w-0">
        <RuleGroup
          node={tree}
          root={tree}
          fields={fields}
          counts={counts}
          counting={preview.status === 'pending'}
          issues={issueText}
          actions={actions}
          canAdd={canAdd}
          onChooseDateFormat={onChooseDateFormat}
          disabled={disabled}
        />
      </div>
      <RuleResultPanel className="lg:sticky lg:top-20" preview={preview} people={index.people} blocked={blocked} />
    </div>
  );
}
